// components/industry-templates/HospitalityEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Hotel, UtensilsCrossed, Star, Award } from 'lucide-react';

export const HospitalityEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Hotel Showcase */}
      {industryData?.hotel_showcase && (
        <Card className="border-0 bg-gradient-to-br from-amber-500/20 to-orange-500/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Hotel className="w-8 h-8" style={{ color: theme.primary }} />
              Featured Hotels & Resorts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {industryData.hotel_showcase.map((hotel: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border border-white/20">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{hotel.name}</h3>
                      <p className="text-slate-300 text-sm">{hotel.location}</p>
                    </div>
                    <div className="flex gap-1">
                      {[...Array(hotel.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                      ))}
                    </div>
                  </div>
                  {hotel.special_offer && (
                    <div className="bg-white/5 rounded p-3 mb-3">
                      <p className="text-white text-sm font-bold mb-1">Special Offer:</p>
                      <p className="text-slate-300 text-sm">{hotel.special_offer}</p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Badge style={{ backgroundColor: theme.primary }}>
                      {hotel.category}
                    </Badge>
                    {hotel.discount && (
                      <Badge className="bg-red-500">{hotel.discount} OFF</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Culinary Experience */}
      {industryData?.culinary_showcase && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <UtensilsCrossed className="w-6 h-6" style={{ color: theme.primary }} />
              Culinary Experience
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {industryData.culinary_showcase.map((experience: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl">
                  <h3 className="text-xl font-bold text-white mb-2">{experience.name}</h3>
                  <p className="text-slate-300 mb-3">{experience.description}</p>
                  <div className="flex gap-2 flex-wrap">
                    <Badge style={{ backgroundColor: theme.primary }}>
                      Chef: {experience.chef}
                    </Badge>
                    <Badge variant="outline" className="border-white/20 text-white">
                      {experience.cuisine}
                    </Badge>
                    {experience.live_demo && (
                      <Badge className="bg-yellow-500 text-black">Live Demo</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Service Standards */}
      {industryData?.service_innovations && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-6 h-6" style={{ color: theme.primary }} />
              Hospitality Innovations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {industryData.service_innovations.map((innovation: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <h4 className="font-bold text-white mb-2">{innovation.name}</h4>
                  <p className="text-sm text-slate-400">{innovation.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Networking Sessions */}
      {industryData?.networking && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white">Industry Networking</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white/5 rounded-lg p-5">
              <p className="text-white mb-4">Connect with industry professionals:</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-white/5 rounded-lg">
                  <p className="text-3xl font-bold" style={{ color: theme.primary }}>
                    {industryData.networking.attendees}+
                  </p>
                  <p className="text-sm text-slate-400">Hotel Professionals</p>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-lg">
                  <p className="text-3xl font-bold" style={{ color: theme.primary }}>
                    {industryData.networking.sessions}
                  </p>
                  <p className="text-sm text-slate-400">Networking Sessions</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
